import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePostHog } from "posthog-js/react";

const signifyInterestSchema = z.object({
  contactName: z.string().min(1, "Contact person name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(1, "Phone number is required"),
});

type SignifyInterestFormData = z.infer<typeof signifyInterestSchema>;

interface SignifyInterestFormProps {
  onSubmit?: (data: SignifyInterestFormData) => void;
  onBack?: () => void;
}

export function SignifyInterestForm({
  onSubmit,
  onBack,
}: SignifyInterestFormProps) {
  const posthog = usePostHog();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignifyInterestFormData>({
    resolver: zodResolver(signifyInterestSchema),
    mode: "onBlur",
  });

  const handleFormSubmit = (data: SignifyInterestFormData) => {
    posthog?.capture('form_submitted', { form_name: 'signify_interest_step_one', success: true });
    onSubmit?.(data);
  };

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold">Signify Interest</h1>
        <p className="mt-2 text-gray-600">
          Provide details to show interest in this opportunity.
        </p>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        {/* Contact Person Name */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Contact Person Name <span className="text-red-500">*</span>
          </label>
          <Input
            {...register("contactName")}
            placeholder="Enter name"
            className="w-full"
          />
          {errors.contactName && (
            <p className="text-red-500 text-sm mt-1">
              {errors.contactName.message}
            </p>
          )}
        </div>

        {/* Email Address */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Email Address <span className="text-red-500">*</span>
          </label>
          <Input
            {...register("email")}
            type="email"
            placeholder="Enter Email"
            className="w-full"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div>

        {/* Phone Number */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <Input
            {...register("phone")}
            type="tel"
            placeholder="Enter Number"
            className="w-full"
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-blue-600 text-blue-600 hover:bg-blue-50 bg-transparent"
            trackingEvent='signify_interest_back'
            trackingProperties={{ step: 1 }}
          >
            Back
          </Button>
          <Button
            type="submit"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            trackingEvent='signify_interest_next'
            trackingProperties={{ step: 1 }}
          >
            {onSubmit ? "Next" : "Submit"}
          </Button>
        </div>
      </form>
    </>
  );
}
